import { del, get, put } from './api-client';
import type { MenuLayout } from '@atlas/shared-types';

interface PreferenceResponse {
  key: string;
  value: string;
  updatedAt: string;
}

export async function getMenuLayout(): Promise<MenuLayout | null> {
  try {
    const response = await get<PreferenceResponse>('/account/preferences/menuLayout');
    return JSON.parse(response.value) as MenuLayout;
  } catch {
    return null;
  }
}

export async function saveMenuLayout(layout: MenuLayout): Promise<void> {
  await put('/account/preferences/menuLayout', {
    value: JSON.stringify(layout),
  });
}

// Generic single-string preference helpers over the same
// GET/PUT /account/preferences/{key} endpoint. The backend requires the stored
// value to be valid JSON, so the string is JSON-encoded on save and decoded on
// read (a bare word like "period" is not valid JSON on its own).
export async function getPreference(key: string): Promise<string | null> {
  try {
    const response = await get<PreferenceResponse>(`/account/preferences/${encodeURIComponent(key)}`);
    const parsed: unknown = JSON.parse(response.value);
    return typeof parsed === 'string' ? parsed : null;
  } catch {
    // 404 (never set) or any transient error → treat as "no saved value".
    return null;
  }
}

export async function savePreference(key: string, value: string): Promise<void> {
  await put(`/account/preferences/${encodeURIComponent(key)}`, { value: JSON.stringify(value) });
}

/** Removes a saved preference (DELETE /account/preferences/{key}); 404 if it was never set. */
export async function deletePreference(key: string): Promise<void> {
  await del(`/account/preferences/${encodeURIComponent(key)}`);
}
